import type { EventData } from "./types";

const DEFAULT_EVENT_DURATION_MS = 2 * 60 * 60 * 1000; // 2 hours
const ALL_DAY_DURATION_MS = 14 * 60 * 60 * 1000; // All-day events normalized to 10:00 remain active until midnight (+14h)

/**
 * Checks whether an event has ended relative to a reference time.
 * For all-day events, stays upcoming through midnight at the end of the final day.
 * If endDate is provided, it is used. Otherwise defaults to 2 hours after date.
 */
export function isEventPast(
    event: Pick<EventData, "date" | "endDate" | "isAllDay">,
    now: Date = new Date()
): boolean {
    const startDate = new Date(event.date);
    if (isNaN(startDate.getTime())) {
        return true;
    }

    let effectiveEndTime: number;
    if (event.isAllDay) {
        const lastDay = event.endDate ? new Date(event.endDate) : startDate;
        const baseTime = isNaN(lastDay.getTime()) ? startDate.getTime() : lastDay.getTime();
        effectiveEndTime = baseTime + ALL_DAY_DURATION_MS;
    } else {
        const endDate = event.endDate
            ? new Date(event.endDate)
            : new Date(startDate.getTime() + DEFAULT_EVENT_DURATION_MS);
        effectiveEndTime = isNaN(endDate.getTime()) ? startDate.getTime() : endDate.getTime();
    }

    return effectiveEndTime < now.getTime();
}

/**
 * Checks whether an event is upcoming.
 * An event is upcoming if it has not concluded and is not explicitly flagged false.
 */
export function isEventUpcoming(
    event: Pick<EventData, "date" | "endDate" | "isAllDay" | "isUpcoming">,
    now: Date = new Date()
): boolean {
    if (event.isUpcoming === false) {
        return false;
    }

    return !isEventPast(event, now);
}

/**
 * Filters for upcoming events and sorts them chronologically (earliest first).
 */
export function filterUpcomingEvents(events: EventData[], now: Date = new Date()): EventData[] {
    return events
        .filter((event) => isEventUpcoming(event, now))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

/**
 * Filters for past events and sorts them in reverse chronological order (most recent first).
 */
export function filterPastEvents(events: EventData[], now: Date = new Date()): EventData[] {
    return events
        .filter((event) => !isEventUpcoming(event, now))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

/**
 * Selects upcoming events for display on the home page, guaranteeing that any
 * events flagged with #highlight are included rather than crowded out by routine services.
 */
export function selectHomeUpcomingEvents(events: EventData[], max = 3, now: Date = new Date()): EventData[] {
    const upcoming = filterUpcomingEvents(events, now);
    if (upcoming.length <= max) return upcoming;

    const highlighted = upcoming.filter((e) => e.isHighlighted);
    if (highlighted.length === 0) {
        return upcoming.slice(0, max);
    }

    const selected: EventData[] = [];
    const selectedKeys = new Set<string>();
    const getKey = (e: EventData) => `${e.title}_${e.date}`;

    // Always include the next immediate chronological gathering
    selected.push(upcoming[0]);
    selectedKeys.add(getKey(upcoming[0]));

    // Add up to (max - 1) highlighted events
    for (const h of highlighted) {
        if (selected.length >= max) break;
        const key = getKey(h);
        if (!selectedKeys.has(key)) {
            selected.push(h);
            selectedKeys.add(key);
        }
    }

    // Fill remaining slots with next chronological events
    for (const u of upcoming) {
        if (selected.length >= max) break;
        const key = getKey(u);
        if (!selectedKeys.has(key)) {
            selected.push(u);
            selectedKeys.add(key);
        }
    }

    return selected.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}
